import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Upload, AlertTriangle, RotateCcw, Link2, Replace } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { costCurrencySchema, type CostCurrency } from '@threadheads/shared';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { useRole } from '@/hooks/use-role';
import { useLocation } from '@/contexts/LocationContext';
import { inventoryApi } from '@/features/inventory/api/inventoryApi';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { useCostOfGoods } from '@/features/inventory/hooks/useCostOfGoods';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { SkuCostHints } from '@/features/inventory/components/SkuCostDisplay';
import {
  useSetSkuShared,
  isMappingStillLiveError,
  isWaitingForOrdersError,
  useSupplyBlankUsage,
  usePreviewStopOfferingBlanks,
  usePreviewReplaceBlank,
  useReplaceBlank,
  reviewAllowsReplace,
} from '@/features/inventory/hooks/useSharedSkus';
import { ReplaceReview, ReplacementBlankPicker } from '@/features/inventory/components/supply/ReplaceReview';
import { NoteLine } from '@/components/shared/NoteLine';
import { baseGarmentCodeOfSku, baseGarmentHref } from '@/features/base-garments/lib/base-garments-url';
import { useOrganization } from '@/contexts/OrganizationContext';
import { supplyAliasRefusalText } from '@/features/inventory/utils/supplyAliasCopy';
import type { OwnerSharedCatalogItem } from '@/features/product-sync/lib/shared-stock';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const SUPPLIER_EMPTY_VALUE = '__none__';

export type SkuCatalogItem = {
  id: string;
  sku: string;
  title: string | null;
  category: {
    type: string | null;
    color: string | null;
    size: string | null;
  };
  imageUrl: string | null;
  productionType: 'POD' | 'PAP';
  /** BLU-534 trim SKUs are production_type PAP but MANUALLY created — never
   *  Shopify-synced, so the PAP "controlled by Shopify" read-only gates must
   *  not apply to them. */
  isTrim?: boolean;
  isLocked: boolean;
  /** The Default cost as typed, in `skuCostOverrideCurrency`. */
  skuCostOverride: number | null;
  /** The Default cost's currency; null = the org currency. Undefined when the
   *  backend does not send it yet, which hides the currency picker. */
  skuCostOverrideCurrency?: string | null;
  /** The Default cost in the org currency (null with no rate). */
  skuCostOverrideOrg?: number | null;
  hasWac?: boolean;
  hsCode: string | null;
  weightValue: number | null;
  weightUnit: string | null;
  isKeyItem?: boolean;
  /** BLU-711: owner-nominated shared SKU (clients may map it as a fallback pool). */
  isShared?: boolean;
  shopifyInventoryItemId?: string | null;
  erpManagedFields?: Record<string, { managedAt: string; managedBy: string }>;
  /** BLU-271: per-SKU supplier override. null/undefined = inherits the category default. */
  supplierId?: string | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sku: SkuCatalogItem | null;
  onUpdated: () => void | Promise<void>;
  /** When true, modal operates in org-level mode (Products page) regardless of location context. */
  globalMode?: boolean;
};

export default function EditSkuModal({ isOpen, onClose, sku, onUpdated, globalMode }: Props) {
  const { t } = useTranslation('inventory');
  const { currency } = useCurrencyFormatter();
  const { toast } = useToast();
  const { can, isAdmin: isAdminRole } = useRole();
  const { selectedLocation: contextLocation, locations } = useLocation();
  const canManageSkus = can('skus.manage');
  // Cost and the supplier override need skus.costs_manage on the server; the
  // Shopify tracking control stays Admin-only.
  const canManageCosts = can('skus.costs_manage');
  const isAdmin = isAdminRole();
  // In globalMode (Products page) always treat as all-locations even if a location is selected in context
  const selectedLocation = globalMode ? null : contextLocation;
  const isAllLocationsMode = !selectedLocation;

  // Phase 92 (UI-SPEC 3.2, D-23): how many client products print on this
  // blank. One read when a SKU manager opens the Products modal on a blank it
  // shares; nothing for anyone else (RBAC layer 1 matches SkusManage on the
  // server).
  const usageSkuId = isOpen && canManageSkus && globalMode && sku?.isShared ? sku.id : null;
  const usageSkuIds = useMemo(() => (usageSkuId ? [usageSkuId] : null), [usageSkuId]);
  const supplyUsage = useSupplyBlankUsage(usageSkuIds);
  const clientProducts = usageSkuId && supplyUsage.data
    ? (supplyUsage.data.usage.find((u) => u.skuId === usageSkuId)?.clientProducts ?? 0)
    : undefined;
  const [replaceOpen, setReplaceOpen] = useState(false);
  const replaceTriggerRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    setReplaceOpen(false);
  }, [sku?.id, isOpen]);
  const closeReplace = () => {
    setReplaceOpen(false);
    // Focus goes back to the button that opened the panel.
    requestAnimationFrame(() => replaceTriggerRef.current?.focus());
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingSku, setIsDeletingSku] = useState(false);
  const [isSavingLocations, setIsSavingLocations] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeleteSku, setConfirmDeleteSku] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [clearImage, setClearImage] = useState(false);
  const [activeLocationIds, setActiveLocationIds] = useState<Set<string>>(new Set());
  const [initialActiveLocationIds, setInitialActiveLocationIds] = useState<Set<string>>(new Set());

  const [skuCode, setSkuCode] = useState('');
  const [skuTitle, setSkuTitle] = useState('');
  const [type, setType] = useState('');
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const [skuCostOverride, setSkuCostOverride] = useState<string>('');
  const [costCurrency, setCostCurrency] = useState<string>('');
  // Only a backend that sends the field saves it, so an older one never
  // silently drops a picked currency.
  const showCurrencyPicker = sku?.skuCostOverrideCurrency !== undefined;
  // The org currency first, then the other supported codes.
  const costCurrencyOptions = [currency, ...costCurrencySchema.options.filter((code) => code !== currency)];
  const [hsCode, setHsCode] = useState('');
  const [weightValue, setWeightValue] = useState<string>('');
  const [weightUnit, setWeightUnit] = useState<string>('');
  const [isKeyItem, setIsKeyItem] = useState(false);
  // BLU-271: '' = inherit the category default (no per-SKU override).
  const [supplierId, setSupplierId] = useState<string>('');

  const { suppliers } = useSuppliers({ includeInactive: true });
  const sortedSuppliers = useMemo(
    () => [...suppliers].sort((a, b) => a.title.localeCompare(b.title)),
    [suppliers]
  );
  // BLU-271: cheap client-side resolution of the inherited category default —
  // both hooks are already fetched elsewhere in the app, so no new endpoint.
  // Falls back to a generic label when the product type has no matching
  // category-costs row or no default supplier set.
  const { items: costOfGoodsItems } = useCostOfGoods({ enabled: canManageCosts && isOpen });
  const inheritedSupplierName = useMemo(() => {
    const productType = sku?.category.type;
    if (!productType) return null;
    const categoryRow = costOfGoodsItems.find((item) => item.productType === productType);
    const defaultSupplierId = categoryRow?.defaultSupplierId;
    if (!defaultSupplierId) return null;
    return suppliers.find((s) => s.id === defaultSupplierId)?.title ?? null;
  }, [sku?.category.type, costOfGoodsItems, suppliers]);
  const inheritLabel = inheritedSupplierName
    ? t('editSkuModal.inheritSupplier', { supplier: inheritedSupplierName })
    : t('editSkuModal.inheritCategoryDefault');

  const mutation = trpc.inventory.updateSku.useMutation();
  const deleteMutation = trpc.inventory.deleteProducts.useMutation();
  const deleteSkusMutation = trpc.inventory.deleteSkus.useMutation();
  const updateSkuActiveLocationsMutation = trpc.inventory.updateSkuActiveLocations.useMutation();
  const setKeyItemMutation = trpc.inventory.setKeyItem.useMutation();
  const locationBreakdownQuery = trpc.inventory.getProductLocationBreakdown.useQuery(
    { skuId: sku?.id ?? '' },
    {
      enabled: isOpen && Boolean(sku?.id) && confirmDeleteSku,
      staleTime: 0,
    }
  );
  const activeLocationsQuery = trpc.inventory.getSkuActiveLocations.useQuery(
    { skuId: sku?.id ?? '' },
    {
      enabled: isOpen && Boolean(sku?.id) && isAllLocationsMode,
      staleTime: 15_000,
    }
  );
  // Per-location inventory (stock + WAC) for the SKU being edited
  const locationInventoryQuery = trpc.inventory.getProductLocationBreakdown.useQuery(
    { skuId: sku?.id ?? '' },
    {
      enabled: isOpen && Boolean(sku?.id) && isAllLocationsMode,
      staleTime: 30_000,
    }
  );
  const locationInventoryMap = useMemo(() => {
    const map = new Map<string, { stock: number; wac: number | undefined }>();
    for (const loc of locationInventoryQuery.data?.locations ?? []) {
      map.set(loc.locationId, {
        stock: loc.onHand ?? loc.stockLevel ?? 0,
        wac: loc.weightedAverageCost,
      });
    }
    return map;
  }, [locationInventoryQuery.data]);

  const wacLocationCount = useMemo(() => {
    let count = 0;
    for (const v of locationInventoryMap.values()) {
      if (v.wac != null) count++;
    }
    return count;
  }, [locationInventoryMap]);

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => a.name.localeCompare(b.name)),
    [locations]
  );
  const hasLocationChanges = useMemo(() => {
    if (activeLocationIds.size !== initialActiveLocationIds.size) return true;
    for (const locationId of activeLocationIds) {
      if (!initialActiveLocationIds.has(locationId)) return true;
    }
    return false;
  }, [activeLocationIds, initialActiveLocationIds]);

  useEffect(() => {
    if (!isOpen || !sku) return;
    setSkuCode(sku.sku);
    setSkuTitle(sku.title ?? '');
    setType(sku.category.type ?? '');
    setColor(sku.category.color ?? '');
    setSize(sku.category.size ?? '');
    setSkuCostOverride(sku.skuCostOverride === null || sku.skuCostOverride === undefined ? '' : String(sku.skuCostOverride));
    setCostCurrency(sku.skuCostOverrideCurrency ?? currency);
    setHsCode(sku.hsCode ?? '');
    setWeightValue(sku.weightValue === null || sku.weightValue === undefined ? '' : String(sku.weightValue));
    setWeightUnit(sku.weightUnit ?? '');
    setSupplierId(sku.supplierId ?? '');
    setImageFile(null);
    setClearImage(false);
    setImagePreview(sku.imageUrl ?? null);
    setConfirmDelete(false);
    setConfirmDeleteSku(false);
    setIsKeyItem(Boolean(sku.isKeyItem));
  }, [isOpen, sku, currency]);

  useEffect(() => {
    if (!isOpen || !isAllLocationsMode) return;
    if (!activeLocationsQuery.data) return;
    const next = new Set<string>(activeLocationsQuery.data.locationIds ?? []);
    setActiveLocationIds(next);
    setInitialActiveLocationIds(new Set(next));
  }, [isOpen, isAllLocationsMode, activeLocationsQuery.data]);

  useEffect(() => {
    if (!isOpen) return;
    if (isAllLocationsMode) return;
    setActiveLocationIds(new Set());
    setInitialActiveLocationIds(new Set());
  }, [isOpen, isAllLocationsMode]);

  const title = useMemo(() => {
    return sku?.sku ? t('editSkuModal.editSkuTitle', { sku: sku.sku }) : t('editSkuModal.editSku');
  }, [sku?.sku, t]);

  // Phase 42: Count ERP-managed fields for summary display
  const erpFieldCount = useMemo(() => {
    if (!sku?.erpManagedFields) return 0;
    return ['title', 'image_url'].filter(f => sku.erpManagedFields?.[f]).length;
  }, [sku?.erpManagedFields]);

  // Phase 42: Reset a field back to Shopify control
  const handleResetField = async (field: 'title' | 'image_url') => {
    if (!sku) return;
    try {
      await mutation.mutateAsync({
        skuId: sku.id,
        productionType: sku.productionType ?? 'POD',
        // Phase 42: releaseFields — cast needed until tRPC type inference picks up Zod schema field
        ...({ releaseFields: [field] } as Record<string, unknown>),
      });
      sonnerToast.success(`${field === 'title' ? 'Title' : 'Image'} reset to Shopify`);
      await onUpdated();
    } catch (_err) {
      sonnerToast.error('Failed to reset field');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setClearImage(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setClearImage(true);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sku) return;

    try {
      setIsSubmitting(true);

      let imageUrl: string | undefined;
      if (imageFile) {
        imageUrl = await inventoryApi.uploadProductImage(imageFile, sku.sku);
      } else if (clearImage) {
        imageUrl = '';
      }

      const costOverrideValue = skuCostOverride.trim() === '' ? null : Number(skuCostOverride);
      if (costOverrideValue !== null && (!Number.isFinite(costOverrideValue) || costOverrideValue < 0)) {
        toast({
          title: 'Invalid default cost',
          description: 'Cost must be a non-negative number.',
          variant: 'destructive',
        });
        return;
      }

      if (type.trim() === '') {
        toast({
          title: 'Type is required',
          description: 'Please enter a product type (base garment).',
          variant: 'destructive',
        });
        return;
      }

      const skuCodeTrimmed = skuCode.trim().toUpperCase();
      if (!skuCodeTrimmed) {
        toast({
          title: 'SKU code is required',
          description: 'Please enter a SKU code.',
          variant: 'destructive',
        });
        return;
      }

      const hsCodeValue = hsCode.trim() || null;
      const weightVal = weightValue.trim() === '' ? null : Number(weightValue);
      const weightUnitVal = weightUnit || null;
      if (weightVal !== null && (!Number.isFinite(weightVal) || weightVal < 0)) {
        toast({
          title: 'Invalid weight',
          description: 'Weight must be a non-negative number.',
          variant: 'destructive',
        });
        return;
      }

      await mutation.mutateAsync({
        skuId: sku.id,
        // Only send sku if changed (avoids unnecessary uniqueness check)
        ...(skuCodeTrimmed !== sku.sku ? { sku: skuCodeTrimmed } : {}),
        title: skuTitle,
        category: {
          type,
          color,
          size,
        },
        imageUrl,
        skuCostOverride: canManageCosts ? costOverrideValue : undefined,
        // null = the org currency. A cleared cost never sends a currency.
        skuCostOverrideCurrency:
          canManageCosts && showCurrencyPicker
            ? (costOverrideValue === null || costCurrency === currency ? null : (costCurrency as CostCurrency))
            : undefined,
        productionType: sku?.productionType ?? 'POD',
        hsCode: hsCodeValue,
        weightValue: weightVal,
        weightUnit: weightUnitVal as 'GRAMS' | 'KILOGRAMS' | 'OUNCES' | 'POUNDS' | null,
        supplierId: canManageCosts ? (supplierId.trim() === '' ? null : supplierId) : undefined,
      });

      toast({
        title: 'SKU Updated',
        description: `${sku.sku} metadata has been updated.`,
      });

      await onUpdated();
      onClose();
    } catch (err) {
      toast({
        title: 'Update Failed',
        // BLU-1400: a supply alias (the 3PL's garment) is refused as one sentence, never the marker.
        // This modal edits the caller's own catalog, so the SKU in hand is the client's own row.
        description: supplyAliasRefusalText(t, err, { row: sku }) ?? (err instanceof Error ? err.message : 'Unknown error'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFromLocation = async () => {
    if (!sku) return;

    if (!selectedLocation) {
      toast({
        title: 'Location required',
        description: 'Select a location in the header before deleting this SKU.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync({
        locationId: selectedLocation.id,
        productIds: [sku.id],
      });

      const restoreHint = 'You can recreate it from Products if needed.';

      toast({
        title: 'SKU Removed',
        description: `${sku.sku} removed from ${selectedLocation.name}. ${restoreHint}`,
      });

      await onUpdated();
      onClose();
    } catch (err) {
      toast({
        title: 'Delete Failed',
        description: supplyAliasRefusalText(t, err, { row: sku }) ?? (err instanceof Error ? err.message : 'Unknown error'),
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleDeleteSku = async () => {
    if (!sku) return;

    try {
      setIsDeletingSku(true);
      await deleteSkusMutation.mutateAsync({ skuIds: [sku.id] });

      toast({
        title: 'SKU Deleted',
        description: `${sku.sku} has been permanently deleted from all locations.`,
      });

      await onUpdated();
      onClose();
    } catch (err) {
      toast({
        title: 'Delete Failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingSku(false);
      setConfirmDeleteSku(false);
    }
  };

  const toggleActiveLocation = (locationId: string) => {
    setActiveLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  };

  const handleSaveActiveLocations = async () => {
    if (!sku || !isAllLocationsMode) return;

    try {
      setIsSavingLocations(true);
      const nextLocationIds = Array.from(activeLocationIds);

      const result = await updateSkuActiveLocationsMutation.mutateAsync({
        skuId: sku.id,
        locationIds: nextLocationIds,
      });

      setInitialActiveLocationIds(new Set(result.activeLocationIds));
      toast({
        title: 'Locations Updated',
        description: `${sku.sku} is now active in ${result.activeLocationIds.length} location${result.activeLocationIds.length === 1 ? '' : 's'}.`,
      });

      await onUpdated();
    } catch (err) {
      toast({
        title: 'Failed to Update Locations',
        description: supplyAliasRefusalText(t, err, { row: sku }) ?? (err instanceof Error ? err.message : 'Unknown error'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingLocations(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {(sku?.productionType === 'PAP' && !sku?.isTrim) && erpFieldCount > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-medium text-foreground">{erpFieldCount}</span> of 2 fields manually edited
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Left column: SKU details & fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sku-code">{t('editSkuModal.skuLabel')}</Label>
                <Input
                    id="sku-code"
                    value={skuCode}
                    onChange={(e) => setSkuCode(e.target.value)}
                    className="font-mono"
                  />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="sku-title">{t('editSkuModal.titleLabel')}</Label>
                  {(sku?.productionType === 'PAP' && !sku?.isTrim) && (
                    <>
                      {sku?.erpManagedFields?.title ? (
                        <Badge className="text-[10px] px-1.5 py-0.5 h-auto bg-blue-50 text-blue-700 border border-blue-200">
                          PrintRun
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto text-muted-foreground">
                          Shopify
                        </Badge>
                      )}
                      {sku?.erpManagedFields?.title && (
                        <button
                          type="button"
                          onClick={() => void handleResetField('title')}
                          className="h-4 w-4 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          aria-label={t('editSkuModal.resetTitleToShopify')}
                          title={t('editSkuModal.resetToShopify')}
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      )}
                    </>
                  )}
                </div>
                <Input
                    id="sku-title"
                    value={skuTitle}
                    onChange={(e) => setSkuTitle(e.target.value)}
                    placeholder={t('editSkuModal.optional')}
                  />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category.type">{t('editSkuModal.typeLabel')}</Label>
                  <Input
                    id="category.type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    disabled={(sku?.productionType === 'PAP' && !sku?.isTrim)}
                    className={(sku?.productionType === 'PAP' && !sku?.isTrim) ? 'opacity-60' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category.color">{t('editSkuModal.colorLabel')}</Label>
                  <Input
                    id="category.color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    disabled={(sku?.productionType === 'PAP' && !sku?.isTrim)}
                    className={(sku?.productionType === 'PAP' && !sku?.isTrim) ? 'opacity-60' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category.size">{t('editSkuModal.sizeLabel')}</Label>
                  <Input
                    id="category.size"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    disabled={(sku?.productionType === 'PAP' && !sku?.isTrim)}
                    className={(sku?.productionType === 'PAP' && !sku?.isTrim) ? 'opacity-60' : ''}
                  />
                </div>
              </div>
              {(sku?.productionType === 'PAP' && !sku?.isTrim) && (
                <p className="text-xs text-muted-foreground">{t('editSkuModal.shopifyControlled')}</p>
              )}

              {canManageCosts && (
                <div className="space-y-2">
                  <Label htmlFor="skuCostOverride">{t('editSkuModal.costOverrideLabel')}</Label>
                  {/* Wraps rather than scrolling the dialog sideways. */}
                  <div className="flex flex-wrap gap-2">
                    <Input
                      id="skuCostOverride"
                      type="number"
                      step="0.01"
                      min="0"
                      value={skuCostOverride}
                      onChange={(e) => setSkuCostOverride(e.target.value)}
                      placeholder={t('editSkuModal.optional')}
                      className="min-w-32 flex-1"
                    />
                    {showCurrencyPicker && (
                      <Select value={costCurrency} onValueChange={setCostCurrency}>
                        <SelectTrigger
                          className="w-24 font-mono uppercase"
                          aria-label={t('editSkuModal.costCurrencyLabel')}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {costCurrencyOptions.map((code) => (
                            <SelectItem key={code} value={code} className="font-mono uppercase">
                              {code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <SkuCostHints
                    currency={currency}
                    inputValue={skuCostOverride}
                    savedOverride={sku?.skuCostOverrideOrg ?? null}
                    hasWac={Boolean(sku?.hasWac)}
                    wacLocationCount={wacLocationCount}
                    locationCount={sortedLocations.length}
                  />
                </div>
              )}

              {canManageCosts && (
                <div className="space-y-2">
                  <Label htmlFor="supplierId">{t('editSkuModal.supplierOverrideLabel')}</Label>
                  <Select
                    value={supplierId || SUPPLIER_EMPTY_VALUE}
                    onValueChange={(value) => setSupplierId(value === SUPPLIER_EMPTY_VALUE ? '' : value)}
                  >
                    <SelectTrigger id="supplierId">
                      <SelectValue placeholder={inheritLabel} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SUPPLIER_EMPTY_VALUE}>{inheritLabel}</SelectItem>
                      {sortedSuppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.is_active
                            ? supplier.title
                            : t('editSkuModal.supplierInactive', { supplier: supplier.title })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('editSkuModal.supplierOverrideHelp')}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="hsCode">{t('editSkuModal.hsCodeLabel')}</Label>
                <Input
                  id="hsCode"
                  value={hsCode}
                  onChange={(e) => setHsCode(e.target.value)}
                  placeholder={t('editSkuModal.hsCodePlaceholder')}
                  maxLength={20}
                                 />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="weightValue">{t('editSkuModal.weightLabel')}</Label>
                  <Input
                    id="weightValue"
                    type="number"
                    step="0.01"
                    min="0"
                    value={weightValue}
                    onChange={(e) => setWeightValue(e.target.value)}
                    placeholder={t('editSkuModal.weightPlaceholder')}
                                     />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weightUnit">{t('editSkuModal.unitLabel')}</Label>
                  <select
                    id="weightUnit"
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                     >
                    <option value="">{t('editSkuModal.selectUnit')}</option>
                    <option value="GRAMS">{t('editSkuModal.unitGrams')}</option>
                    <option value="KILOGRAMS">{t('editSkuModal.unitKilograms')}</option>
                    <option value="OUNCES">{t('editSkuModal.unitOunces')}</option>
                    <option value="POUNDS">{t('editSkuModal.unitPounds')}</option>
                  </select>
                </div>
              </div>

              {canManageSkus && (
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="key-item-switch" className="text-sm font-medium">
                      {t('editSkuModal.keyItemLabel')}
                    </Label>
                    <p className="text-xs text-muted-foreground">{t('editSkuModal.keyItemHelp')}</p>
                  </div>
                  <Switch
                    id="key-item-switch"
                    checked={isKeyItem}
                    disabled={setKeyItemMutation.isPending}
                    onCheckedChange={(checked) => {
                      setIsKeyItem(checked);
                      if (sku) {
                        setKeyItemMutation.mutate(
                          { skuId: sku.id, isKeyItem: checked },
                          {
                            onSuccess: () => {
                              void onUpdated();
                            },
                            onError: (error) => {
                              // Revert on failure
                              setIsKeyItem(!checked);
                              sonnerToast.error('Failed to update key item status', {
                                description: error.message,
                              });
                            },
                          }
                        );
                      }
                    }}
                  />
                </div>
              )}

              {/* BLU-711: is_shared is an org-level nomination (the owner
                  offers this SKU as a fallback pool to mapped clients), so it
                  only renders in globalMode — the Products page — never in a
                  per-location context. */}
              {canManageSkus && globalMode && sku && (
                <SharedSkuToggle
                  skuId={sku.id}
                  initialShared={sku.isShared ?? false}
                  onUpdated={onUpdated}
                  baseGarmentsHref={blankBaseGarmentsHref(sku)}
                  supplyUsage={
                    usageSkuId
                      ? {
                          clientProducts,
                          loading: supplyUsage.loading,
                          refresh: supplyUsage.retry,
                        }
                      : undefined
                  }
                  replaceOpen={replaceOpen}
                  replacePanelId={`replace-panel-${sku.id}`}
                  onOpenReplace={(trigger) => {
                    replaceTriggerRef.current = trigger;
                    setReplaceOpen(true);
                  }}
                />
              )}

              {isAdmin && sku?.shopifyInventoryItemId && (
                <TrackingToggle skuId={sku.id} productionType={sku.productionType} onUpdated={onUpdated} />
              )}
            </div>

            {/* Right column: Active Locations & Image */}
            <div className="space-y-4">
              {isAllLocationsMode && (
                <div className="space-y-2 rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <Label>{t('editSkuModal.activeLocations')}</Label>
                    {(activeLocationsQuery.isFetching || isSavingLocations) && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                        {t('updating')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{t('editSkuModal.sparseMode')}</p>
                  {activeLocationsQuery.isLoading ? (
                    <div className="text-xs text-muted-foreground">{t('editSkuModal.loadingLocations')}</div>
                  ) : activeLocationsQuery.error ? (
                    <div className="text-xs text-red-600">{activeLocationsQuery.error.message}</div>
                  ) : sortedLocations.length === 0 ? (
                    <div className="text-xs text-muted-foreground">{t('editSkuModal.noLocations')}</div>
                  ) : (
                    // Never wider than the box (DESIGN.md section 22): the
                    // Location column takes the slack (`w-full`) and its names
                    // wrap anywhere, so the numbers and the Active checkbox
                    // keep their width. A truncated name capped at 140px used
                    // to push the table past the box and scroll it sideways.
                    <div className="max-h-56 overflow-y-auto rounded-md border">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50 text-muted-foreground">
                            <th className="w-full text-left font-medium px-2 py-1.5">{t('editSkuModal.colLocation')}</th>
                            <th className="text-right font-medium px-2 py-1.5">{t('editSkuModal.colStock')}</th>
                            <th className="text-right font-medium px-2 py-1.5">{t('editSkuModal.colCost')}</th>
                            <th className="text-center font-medium px-2 py-1.5 w-12">{t('editSkuModal.colActive')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {sortedLocations.map((location) => {
                            const inv = locationInventoryMap.get(location.id);
                            return (
                              <tr key={location.id} className="hover:bg-muted/30">
                                <td className="px-2 py-1.5 [overflow-wrap:anywhere]">{location.name}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                                  {inv?.stock ?? '-'}
                                </td>
                                <td className="px-2 py-1.5 text-right tabular-nums">
                                  {inv?.wac != null ? (
                                    <span className="font-medium">${inv.wac.toFixed(2)}</span>
                                  ) : sku?.skuCostOverrideOrg != null ? (
                                    <span className="text-muted-foreground">
                                      ${sku.skuCostOverrideOrg.toFixed(2)}{' '}
                                      <span className="text-[10px]">{t('editSkuModal.defaultCostTag')}</span>
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <Checkbox
                                    checked={activeLocationIds.has(location.id)}
                                    onCheckedChange={() => toggleActiveLocation(location.id)}
                                    disabled={isSavingLocations}
                                    aria-label={t('editSkuModal.activeAtAria', { location: location.name })}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleSaveActiveLocations()}
                      disabled={
                        isSavingLocations ||
                        activeLocationsQuery.isLoading ||
                        activeLocationsQuery.isError ||
                        !hasLocationChanges
                      }
                    >
                      {isSavingLocations ? t('editSkuModal.savingLocations') : t('editSkuModal.saveLocations')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('editSkuModal.skuImage')}</Label>
                <div className="flex flex-col items-center justify-center border border-dashed border-gray-300 rounded-md p-4">
                  <Label
                    htmlFor="image-upload"
                    className="cursor-pointer flex flex-col items-center justify-center w-full h-full"
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt={t('editSkuModal.skuImage')}
                        className="max-h-40 object-contain mb-2"
                      />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-500">{t('editSkuModal.clickToUploadImage')}</span>
                      </>
                    )}
                  </Label>
                  <Input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  {(imagePreview || sku?.imageUrl) && (
                    <Button type="button" variant="ghost" size="sm" onClick={handleRemoveImage} className="mt-2">
                      {t('editSkuModal.removeImage')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Phase 92 (UI-SPEC 3.2): "Replace with" opens inline in this
              dialog, never a dialog from a dialog (DESIGN.md section 10). It
              spans both columns so the four-column review never squeezes
              into one (section 22). SKU managers on a blank they share only. */}
          {canManageSkus && globalMode && sku && replaceOpen && (
            <SharedSkuReplacePanel
              id={`replace-panel-${sku.id}`}
              skuId={sku.id}
              skuCode={sku.sku}
              productType={sku.category.type}
              onClose={closeReplace}
              onReplaced={() => {
                supplyUsage.retry();
                closeReplace();
              }}
            />
          )}

          {/* Delete SKU confirmation panel (Products page only) */}
          {confirmDeleteSku && !selectedLocation && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <div className="text-sm text-red-900">
                  <p className="font-medium">{t('editSkuModal.permanentlyDelete', { sku: sku?.sku ?? '' })}</p>
                  <p className="mt-1">{t('editSkuModal.deleteSkuDesc')}</p>
                </div>
              </div>
              {locationBreakdownQuery.isLoading ? (
                <div className="flex items-center gap-1 text-xs text-muted-foreground pl-6">
                  <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  {t('editSkuModal.checkingInventory')}
                </div>
              ) : locationBreakdownQuery.data?.locations?.some(
                  (loc) => loc.onHand > 0 || loc.committed > 0 || loc.unavailable > 0
                ) ? (
                <div className="pl-6 space-y-1">
                  <p className="text-xs font-medium text-red-800">{t('editSkuModal.stockExistsAt')}</p>
                  <ul className="text-xs text-red-700 space-y-0.5">
                    {locationBreakdownQuery.data.locations
                      .filter((loc) => loc.onHand > 0 || loc.committed > 0 || loc.unavailable > 0)
                      .map((loc) => (
                        <li key={loc.locationId}>
                          {loc.locationName}: {loc.onHand} on hand
                          {loc.committed > 0 && `, ${loc.committed} committed`}
                          {loc.unavailable > 0 && `, ${loc.unavailable} unavailable`}
                        </li>
                      ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter className="pt-6">
            <Button
              variant="outline"
              type="button"
              onClick={onClose}
              disabled={isDeleting || isDeletingSku || isSubmitting || isSavingLocations}
            >
              {t('editSkuModal.cancel')}
            </Button>
            {selectedLocation && confirmDelete ? (
              <>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  disabled={isDeleting}
                >
                  {t('editSkuModal.cancelDelete')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void handleDeleteFromLocation()}
                  disabled={isDeleting}
                >
                  {isDeleting ? t('editSkuModal.deleting') : t('editSkuModal.confirmDelete')}
                </Button>
              </>
            ) : confirmDeleteSku && !selectedLocation ? (
              <>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setConfirmDeleteSku(false)}
                  disabled={isDeletingSku}
                >
                  {t('editSkuModal.cancelDelete')}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void handleDeleteSku()}
                  disabled={isDeletingSku || locationBreakdownQuery.isLoading}
                >
                  {isDeletingSku ? t('editSkuModal.deleting') : t('editSkuModal.confirmDeleteSku')}
                </Button>
              </>
            ) : (
              <>
                {selectedLocation && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setConfirmDelete(true)}
                    disabled={isDeleting || isSubmitting || isSavingLocations}
                  >
                    {t('editSkuModal.deleteFromLocation')}
                  </Button>
                )}
                {!selectedLocation && canManageSkus && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setConfirmDeleteSku(true)}
                    disabled={isDeletingSku || isSubmitting || isSavingLocations}
                  >
                    {t('editSkuModal.deleteSku')}
                  </Button>
                )}
                <Button type="submit" disabled={isSubmitting || mutation.isPending || isDeleting || isDeletingSku || isSavingLocations}>
                  {isSubmitting ? t('editSkuModal.saving') : t('editSkuModal.save')}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// BLU-343: Per-SKU Shopify inventory tracking toggle
/**
 * F24: who can use a blank is set per code in Base garments, so a blank whose
 * SKU is base-garment shaped gets a link there instead of the shared switch.
 * Anything else (a design, a trim, a PAP SKU, a legacy blank code) keeps it.
 */
function blankBaseGarmentsHref(sku: SkuCatalogItem): string | undefined {
  if (sku.isLocked || sku.isTrim || sku.productionType !== 'POD') return undefined;
  const code = baseGarmentCodeOfSku(sku.sku);
  return code ? baseGarmentHref(code) : undefined;
}

/**
 * BLU-711: owner-side "Shared SKU" nomination toggle (globalMode only).
 * Enabling applies immediately (Key Item pattern: optimistic + revert).
 *
 * Phase 92 (UI-SPEC 3.1 and 3.2, D-23): turning it off is "Stop offering".
 * - "Used by {count} client products" sits under the help with an outline
 *   "Replace with" beside it, from one usage read the modal owns
 *   (`supplyUsage`, admins on a blank they share only).
 * - The confirm reads the Stop offering review for this blank (one read, only
 *   while the confirm is open). Client products on the blank block the stop
 *   and offer "Replace with" instead; back-up mappings keep today's
 *   delete-the-mapping-first guidance; a clear blank gets the orange "Stop
 *   offering blank". Drained supply mappings do not block: the server
 *   releases them (92-30). The server re-checks inside the write, so a refusal
 *   (P0711 / CONFLICT) still lands on the guidance and re-reads the usage.
 * - 92-31b (92-30 review M2): a blank only orders already placed still hold
 *   (the review's `waitingForOrders`, or a refusal carrying the waiting
 *   marker) says so, with no stop button, instead of the back-up mapping
 *   guidance that does not apply to it.
 */
export function SharedSkuToggle({
  skuId,
  initialShared,
  onUpdated,
  supplyUsage,
  replaceOpen = false,
  replacePanelId,
  onOpenReplace,
  baseGarmentsHref,
}: {
  skuId: string;
  initialShared: boolean;
  onUpdated: () => void | Promise<void>;
  /**
   * A blank whose who-can-use lives in Base garments (F24): the switch gives
   * way to a link there. Usage and Replace stay.
   */
  baseGarmentsHref?: string;
  /** The modal's usage read for this blank; absent when it is not shared. */
  supplyUsage?: { clientProducts: number | undefined; loading: boolean; refresh: () => void };
  replaceOpen?: boolean;
  replacePanelId?: string;
  /** Opens the inline Replace panel; the trigger gets focus back on close. */
  onOpenReplace?: (trigger: HTMLButtonElement) => void;
}) {
  const { t } = useTranslation('inventory');
  const [isShared, setIsShared] = useState(initialShared);
  const [confirming, setConfirming] = useState(false);
  const [mappingGuidance, setMappingGuidance] = useState(false);
  const [waitingGuidance, setWaitingGuidance] = useState(false);
  const stopReviewIds = useMemo(() => (confirming ? [skuId] : null), [confirming, skuId]);
  const stopReview = usePreviewStopOfferingBlanks(stopReviewIds);
  const mutation = useSetSkuShared();

  useEffect(() => {
    setIsShared(initialShared);
    setConfirming(false);
    setMappingGuidance(false);
    setWaitingGuidance(false);
  }, [skuId, initialShared]);

  const applyShared = (next: boolean) => {
    setIsShared(next);
    mutation.mutate(
      { skuId, isShared: next },
      {
        onSuccess: () => {
          setConfirming(false);
          setMappingGuidance(false);
          setWaitingGuidance(false);
          void onUpdated();
        },
        onError: (error) => {
          // Revert on failure (Key Item pattern).
          setIsShared(!next);
          if (!next && isWaitingForOrdersError(error)) {
            // Orders already placed still hold it: nothing to remove, it
            // frees itself once they are finished. Checked first: the same
            // message also reads as a live-mapping conflict.
            setWaitingGuidance(true);
            stopReview.retry();
            supplyUsage?.refresh();
          } else if (!next && isMappingStillLiveError(error)) {
            // Guidance, not a raw error: something still uses the blank.
            setMappingGuidance(true);
            stopReview.retry();
            supplyUsage?.refresh();
          } else {
            sonnerToast.error(t('sharedSkus.updateFailed'), {
              description: error.message,
            });
          }
        },
      }
    );
  };

  const reviewLoading = confirming && (stopReview.loading || !stopReview.data) && !stopReview.error;
  const inUse = stopReview.data?.inUse.find((entry) => entry.skuId === skuId);
  const reviewKnown = Boolean(stopReview.data) && !stopReview.error;
  const clientProductsOnBlank = reviewKnown ? (inUse?.clientProducts ?? 0) : supplyUsage?.clientProducts;
  const backupMappings = reviewKnown ? (inUse?.backupMappings ?? 0) : undefined;
  // Client products print on the blank: Replace first (D-23).
  const blockedBySupply = typeof clientProductsOnBlank === 'number' && clientProductsOnBlank > 0;
  // Live back-up mappings make un-sharing impossible server-side: show
  // guidance, not a button that will always 409.
  const blockedByMappings =
    !blockedBySupply && (mappingGuidance || (typeof backupMappings === 'number' && backupMappings > 0));
  // Nothing new uses it, but orders already placed still hold it (92-30
  // review M2): it cannot stop being offered yet.
  const waitingForOrders =
    reviewKnown && (stopReview.data?.waitingForOrders ?? []).some((entry) => entry.skuId === skuId);
  const blockedByWaiting = !blockedBySupply && !blockedByMappings && (waitingGuidance || waitingForOrders);
  const blocked = blockedByMappings || blockedByWaiting;
  const usedBy = isShared ? supplyUsage?.clientProducts : undefined;
  const canReplace = Boolean(onOpenReplace);

  const replaceButton = (key: string) => (
    <Button
      key={key}
      type="button"
      variant="outline"
      size="sm"
      className="h-8 shrink-0 gap-1.5 px-3 text-xs"
      aria-expanded={replaceOpen}
      aria-controls={replaceOpen ? replacePanelId : undefined}
      onClick={(event) => {
        setConfirming(false);
        setMappingGuidance(false);
        setWaitingGuidance(false);
        onOpenReplace?.(event.currentTarget);
      }}
    >
      <Replace className="h-3.5 w-3.5" aria-hidden="true" />
      {t('sharedSkus.replace.open')}
    </Button>
  );

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-0.5">
          <Label
            htmlFor={baseGarmentsHref ? undefined : 'shared-sku-switch'}
            className="flex items-center gap-1.5 text-sm font-medium"
          >
            <Link2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            {t('sharedSkus.toggleLabel')}
          </Label>
          <p className="text-xs text-muted-foreground">
            {baseGarmentsHref ? t('products.blankOpensInBaseGarments') : t('sharedSkus.toggleHelp')}
          </p>
        </div>
        {baseGarmentsHref ? (
          <Button asChild variant="outline" size="sm" className="h-8 shrink-0 px-3 text-xs">
            <Link to={baseGarmentsHref}>{t('products.openInBaseGarments')}</Link>
          </Button>
        ) : (
          <Switch
            id="shared-sku-switch"
            checked={isShared}
            disabled={mutation.isPending}
            onCheckedChange={(checked) => {
              if (checked) {
                setMappingGuidance(false);
                setWaitingGuidance(false);
                applyShared(true);
              } else {
                setConfirming(true);
              }
            }}
          />
        )}
      </div>

      {/* Renders nothing at zero and while loading, so the row never shifts. */}
      {typeof usedBy === 'number' && usedBy > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <NoteLine>{t('sharedSkus.replace.usedBy', { count: usedBy })}</NoteLine>
          {canReplace && !confirming && replaceButton('usage')}
        </div>
      )}

      {confirming && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
            <div className="text-xs leading-normal text-amber-800">
              <p className="font-medium">{t('sharedSkus.confirmDisableTitle')}</p>
              <p className="mt-1" role="status">
                {blockedBySupply
                  ? t('sharedSkus.stopOffering.blocked', { count: clientProductsOnBlank })
                  : blockedByWaiting
                    ? t('sharedSkus.stopOffering.waiting')
                    : reviewLoading
                    ? t('sharedSkus.mappingCountLoading')
                      : typeof backupMappings === 'number'
                        ? t('sharedSkus.confirmDisableBody', { count: backupMappings })
                        : t('sharedSkus.confirmDisableBodyUnknown')}
              </p>
              {blockedByMappings && (
                <p className="mt-1 font-medium" role="alert">
                  {t('sharedSkus.mappingLiveGuidance')}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => {
                setConfirming(false);
                setMappingGuidance(false);
                setWaitingGuidance(false);
              }}
            >
              {t(blocked ? 'sharedSkus.gotIt' : 'sharedSkus.keepShared')}
            </Button>
            {blockedBySupply
              ? canReplace && replaceButton('blocked')
              : !blocked && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 border-orange-300 px-3 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                    disabled={mutation.isPending || reviewLoading}
                    onClick={() => applyShared(false)}
                  >
                    {mutation.isPending && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    )}
                    {t('sharedSkus.confirmDisable')}
                  </Button>
                )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Phase 92 (UI-SPEC 3.2 and 3.4; D-11, D-12, D-23): "Replace with" for one
 * shared blank, inline in `EditSkuModal` (no dialog from a dialog). The
 * picker lists the caller's other shared blanks; the review is one dry run
 * per change of the choice (300 ms debounce, last answer kept); "Replace for
 * new orders" sends one `replaceBlank` and `Cmd/Ctrl+Enter` does the same only
 * when the review has loaded and at least one print SKU changes. Orders
 * already placed stay on the current blank, and nothing is written to any
 * client's store (D-28): each store catches up at its next Sync selected.
 *
 * Endings (review P7, `useReplaceBlank`): success toasts and closes; a retry
 * of a committed replace toasts "already done" and closes; no answer never
 * says nothing changed and re-runs the review; the row cap keeps the confirm
 * off.
 */
export function SharedSkuReplacePanel({
  id,
  skuId,
  skuCode,
  productType,
  onClose,
  onReplaced,
}: {
  id: string;
  skuId: string;
  skuCode: string;
  productType: string | null;
  onClose: () => void;
  /** After a replace (or an already-applied retry): refresh the usage, close. */
  onReplaced: () => void;
}) {
  const { t } = useTranslation('inventory');
  const { organization } = useOrganization();
  const [chosen, setChosen] = useState<OwnerSharedCatalogItem | null>(null);
  const [notice, setNotice] = useState<'maybe_finishing' | 'too_many_rows' | null>(null);
  const pairs = useMemo(() => (chosen ? [{ fromSkuId: skuId, toSkuId: chosen.id }] : null), [chosen, skuId]);
  const review = usePreviewReplaceBlank(pairs);
  const { replace, pending } = useReplaceBlank();
  const pickerRef = useRef<HTMLButtonElement | null>(null);

  // Focus follows the panel open to its first control.
  useEffect(() => {
    pickerRef.current?.focus();
  }, []);

  const canSubmit = Boolean(chosen) && reviewAllowsReplace(review) && !pending && notice !== 'too_many_rows';

  const submit = () => {
    if (!canSubmit || !chosen || !pairs) return;
    replace(pairs, chosen.sku, (outcome) => {
      switch (outcome.kind) {
        case 'replaced':
        case 'already_applied':
          onReplaced();
          return;
        case 'maybe_finishing':
          setNotice('maybe_finishing');
          review.retry();
          return;
        case 'too_many_rows':
          setNotice('too_many_rows');
          return;
        case 'refused':
          review.retry();
          return;
      }
    });
  };

  return (
    <div
      id={id}
      role="region"
      aria-label={t('sharedSkus.replace.panelTitle', { sku: skuCode })}
      className="mt-6 space-y-4 rounded-md border p-4 motion-safe:animate-fade-in"
      onKeyDown={(event) => {
        if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey) || event.nativeEvent.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        submit();
      }}
    >
      <p className="text-sm font-medium">{t('sharedSkus.replace.panelTitle', { sku: skuCode })}</p>
      <ReplacementBlankPicker
        id={`${id}-picker`}
        ownerOrganizationId={organization?.id ?? ''}
        currentSkuId={skuId}
        productType={productType}
        value={chosen}
        onChange={(item) => {
          setChosen(item);
          setNotice(null);
        }}
        triggerRef={pickerRef}
      />
      {chosen && <ReplaceReview review={review} onRetry={review.retry} />}
      {notice && (
        <div role="alert">
          <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal">
            {notice === 'maybe_finishing' ? t('sharedSkus.replace.maybeFinishing') : t('sharedSkus.replace.tooManyRows')}
          </NoteLine>
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 px-3 text-xs" onClick={onClose}>
          {t('sharedSkus.replace.close')}
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
          disabled={!canSubmit}
          onClick={submit}
        >
          {pending && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {t('sharedSkus.replace.confirm')}
        </Button>
      </div>
    </div>
  );
}

function TrackingToggle({
  skuId,
  productionType,
  onUpdated,
}: {
  skuId: string;
  productionType: 'POD' | 'PAP';
  onUpdated: () => void | Promise<void>;
}) {
  // Fetch actual tracking state from Shopify on mount
  const trackingQuery = trpc.stockSync.getSkuTrackingState.useQuery(
    { skuId },
    { staleTime: 30_000 }
  );
  const [isTracked, setIsTracked] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const trackingMutation = trpc.stockSync.setSkuInventoryTracking.useMutation();

  // Sync local state from query result
  useEffect(() => {
    if (trackingQuery.data) {
      setIsTracked(trackingQuery.data.tracked);
    }
  }, [trackingQuery.data]);

  const handleToggle = (checked: boolean) => {
    if (!checked) {
      // Disabling tracking is destructive — show confirmation
      setShowConfirm(true);
      return;
    }
    // Enabling tracking — immediate
    setIsTracked(true);
    trackingMutation.mutate(
      { skuId, tracked: true },
      {
        onSuccess: () => {
          sonnerToast.success('Inventory tracking enabled');
          void onUpdated();
        },
        onError: (error) => {
          setIsTracked(false);
          sonnerToast.error('Failed to enable tracking', { description: error.message });
        },
      }
    );
  };

  const handleConfirmDisable = () => {
    setShowConfirm(false);
    setIsTracked(false);
    trackingMutation.mutate(
      { skuId, tracked: false },
      {
        onSuccess: () => {
          sonnerToast.success('Inventory tracking disabled');
          void onUpdated();
        },
        onError: (error) => {
          setIsTracked(true);
          sonnerToast.error('Failed to disable tracking', { description: error.message });
        },
      }
    );
  };

  return (
    <>
      <div className="flex items-center justify-between rounded-md border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="tracking-switch" className="text-sm font-medium">
            Shopify Inventory Tracking
          </Label>
          <p className="text-xs text-muted-foreground">
            {isTracked
              ? 'Shopify tracks available quantity for this variant'
              : 'Variant is not tracked, so Shopify shows unlimited availability'}
          </p>
          <p className="text-[10px] text-muted-foreground/70">
            {productionType === 'PAP' ? 'PAP: direct mapping' : 'POD: applies to discovered variants'}
          </p>
        </div>
        <Switch
          id="tracking-switch"
          checked={isTracked}
          disabled={trackingMutation.isPending || trackingQuery.isLoading}
          onCheckedChange={handleToggle}
        />
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable inventory tracking?</AlertDialogTitle>
            <AlertDialogDescription>
              This will tell Shopify to stop tracking stock for this product.
              Shopify will show unlimited availability until tracking is re-enabled.
              This affects all locations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDisable}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disable Tracking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
